import { Model } from '@vuex-orm/core'
import SpecialWish from '@/models/user/SpecialWish'
import HotelSearch from '@/models/hotels/HotelSearch'
import Hotel from '@/models/hotels/Hotel'
import Room from '@/models/Room'
import RatePlanPrice from '@/models/RatePlanPrice'
import MealPlan from '@/models/MealPlan'
import Booking from '@/models/user/Booking'
import Extra from '@/models/Extra'
import CheckoutExtra from '@/models/CheckoutExtra'
import Configuration from '@/models/Configuration'
import { eventBus } from '@/main'

let debouncedBookingRecalculationTimer: ReturnType<typeof setTimeout>
function debouncedBookingRecalculation() {
  if (debouncedBookingRecalculationTimer) {
    clearTimeout(debouncedBookingRecalculationTimer)
  }

  debouncedBookingRecalculationTimer = setTimeout(() => {
    eventBus.$emit('recalculate-booking')
  }, 100)
}

export default class Checkout extends Model {
  declare id: number
  declare configurationId: string
  declare configuration: Configuration
  declare identifier: string
  declare bookingId: number
  declare booking: Booking
  declare hotelSearchId: number
  declare hotelSearch: HotelSearch
  declare title: string
  declare firstName: string
  declare middleName: string
  declare surName: string
  declare street: string
  declare addressSuffix?: string
  declare hotelSelectedId: string
  declare hotel: Hotel
  declare roomSelectedId: string
  declare room: Room
  declare ratePlanSelectedId: string
  declare ratePlanPrices: RatePlanPrice
  declare packagePrices: RatePlanPrice
  declare specialOfferPrices: RatePlanPrice
  declare mealPlanSelectedId: string
  declare mealPlan: MealPlan
  declare postcode?: string
  declare city?: string
  declare region?: string
  declare country?: string
  declare language?: string
  declare email?: string
  declare mobilePhone?: string
  declare viaEmail: boolean
  declare viaMobilePhone: boolean
  declare registerParticipants: boolean
  declare specialWishes: SpecialWish
  declare active: boolean
  declare ageGroupIds: string[]
  declare taxAmount: number
  declare feeBreakdown: number
  declare occupancyId: number
  declare extras: Extra[] // via CheckoutExtra
  declare selectedPhysicalRoomId: number | null
  declare selectedPhysicalRoomNumber: number | null
  declare roomStatus: string

  static entity = 'checkout'
  static primaryKey = 'id'

  static fields() {
    return {
      id: this.attr(null),
      configurationId: this.attr(null),
      configuration: this.belongsTo(Configuration, 'configurationId'),
      identifier: this.attr(null),
      bookingId: this.number(1),
      booking: this.belongsTo(Booking, 'bookingId'),
      hotelSearchId: this.number(1),
      hotelSearch: this.belongsTo(HotelSearch, 'hotelSearchId'),
      title: this.attr(null),
      firstName: this.attr(null),
      middleName: this.attr(null),
      surName: this.attr(null),
      street: this.attr(null),
      addressSuffix: this.attr(null),
      hotelSelectedId: this.attr(null),
      hotel: this.belongsTo(Hotel, 'hotelSelectedId'),
      roomSelectedId: this.attr(null),
      room: this.belongsTo(Room, 'roomSelectedId'),
      ratePlanSelectedId: this.attr(null),
      ratePlanPrices: this.belongsTo(RatePlanPrice, 'ratePlanSelectedId'),
      packagePrices: this.belongsTo(RatePlanPrice, 'ratePlanSelectedId'),
      specialOfferPrices: this.belongsTo(RatePlanPrice, 'ratePlanSelectedId'),
      mealPlanSelectedId: this.attr(null),
      mealPlan: this.belongsTo(MealPlan, 'mealPlanSelectedId', 'code'),
      postcode: this.attr(null),
      city: this.attr(null),
      region: this.attr(null),
      country: this.attr(null),
      language: this.attr(null),
      email: this.attr(null),
      mobilePhone: this.attr(null),
      viaEmail: this.boolean(false),
      viaMobilePhone: this.boolean(false),
      registerParticipants: this.boolean(false),
      specialWishes: this.hasOne(SpecialWish, 'checkoutId'),
      active: this.boolean(false),
      ageGroupIds: this.attr([]),
      taxAmount: this.number(0),
      feeBreakdown: this.number(0),
      occupancyId: this.number(0),
      extras: this.belongsToMany(Extra, CheckoutExtra, 'checkout_id', 'extra_id'),
      selectedPhysicalRoomId: this.number(null).nullable(),
      selectedPhysicalRoomNumber: this.number(null).nullable(),
      roomStatus: this.string("")
    }
  }

  get mealPlanName() {
    return (this.mealPlan && this.mealPlan.langedName) || ''
  }

  static afterUpdate() {
    debouncedBookingRecalculation()
  }

  $choose() {
    const currentId = this.id

    this.$update({
      where: () => true,
      data: (record: Checkout) => { record.active = record.id === currentId }
    })
  }

  get totalPrice() {
    return this.ratePlanPrices !== null
      ? this.ratePlanPrices
      : this.packagePrices
  }
}
